import Phaser from 'phaser';
import { BRAND, HEX } from '../config';
import { LEVELS } from '../game/levels';
import { formatTime, getBest, recordTime } from '../game/scores';
import { presentLogical } from '../hidpi';

type ResultData = {
  levelIndex: number;
  win: boolean;
  stars: number;
  passOutput: boolean;
  passStorage?: boolean;
  passBudget: boolean;
  passInverter?: boolean;
  outputW: number;
  targetW: number;
  storageKWh?: number;
  targetKWh?: number;
  spent: number;
  budget: number;
  batsInSun?: number;
  batCreditW?: number;
  elapsedMs?: number;
};

const WIN_LINES = [
  'Juicy work.',
  'That roof is buzzing.',
  'Powering a brighter tomorrow.',
  'Solar gold.',
];

const LOSE_LINES = [
  'Not quite enough juice.',
  'The sun is generous — be more so.',
  'Re-rack and try again.',
];

export class ResultScene extends Phaser.Scene {
  private result!: ResultData;

  constructor() {
    super('ResultScene');
  }

  init(data: ResultData) {
    this.result = data;
  }

  create() {
    const { width, height } = presentLogical(this);

    // Backdrop with subtle sky gradient
    this.cameras.main.setBackgroundColor(BRAND.ink);
    const grad = this.add.graphics();
    grad.fillGradientStyle(BRAND.skyTop, BRAND.skyTop, BRAND.ink, BRAND.ink, 1, 1, 0, 0);
    grad.fillRect(0, 0, width, height);
    this.cameras.main.fadeIn(280, 0x2a, 0x31, 0x40);

    // Card
    const cardW = 540;
    const cardH = 460;
    const cardX = (width - cardW) / 2;
    const cardY = (height - cardH) / 2;
    const card = this.add.graphics();
    // Drop shadow
    card.fillStyle(0x000000, 0.45);
    card.fillRoundedRect(cardX, cardY + 8, cardW, cardH, 14);
    // Body
    card.fillStyle(BRAND.inkSoft, 1);
    card.fillRoundedRect(cardX, cardY, cardW, cardH, 14);
    // Top accent strip
    card.fillStyle(this.result.win ? BRAND.amber : BRAND.red, 1);
    card.fillRoundedRect(cardX, cardY, cardW, 5, { tl: 14, tr: 14, bl: 0, br: 0 });
    // Hairline border
    card.lineStyle(1, 0xffffff, 0.08);
    card.strokeRoundedRect(cardX + 0.5, cardY + 0.5, cardW - 1, cardH - 1, 14);

    // Header
    const headerColor = this.result.win ? HEX.amber : HEX.red;
    this.add
      .text(width / 2, cardY + 28, this.result.win ? 'LEVEL CLEAR' : 'TRY AGAIN', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '14px',
        color: headerColor,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const lines = this.result.win ? WIN_LINES : LOSE_LINES;
    const tagline = lines[Math.floor(Math.random() * lines.length)];
    this.add
      .text(width / 2, cardY + 60, tagline, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '28px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // Stars (with stagger reveal)
    const starsY = cardY + 110;
    const starSize = 32;
    const starGap = 18;
    const starsTotal = 3;
    const starsRowW = starsTotal * starSize + (starsTotal - 1) * starGap;
    const starsX = width / 2 - starsRowW / 2;
    for (let i = 0; i < starsTotal; i++) {
      const sx = starsX + i * (starSize + starGap) + starSize / 2;
      const filled = i < this.result.stars;
      const g = this.drawStar(sx, starsY + starSize / 2, starSize / 2, filled);
      if (filled) {
        g.setAlpha(0);
        g.setScale(0.4);
        this.tweens.add({
          targets: g,
          alpha: 1,
          scale: 1,
          duration: 320,
          delay: 240 + i * 180,
          ease: 'Back.easeOut',
        });
      }
    }

    // Stats
    const statY = cardY + 170;
    const outputColor = this.result.passOutput ? HEX.amber : HEX.red;
    this.add
      .text(cardX + 36, statY, 'Output', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '11px',
        color: '#a1a1a1',
        fontStyle: 'bold',
      });
    this.add.text(
      cardX + 36,
      statY + 14,
      `${(this.result.outputW / 1000).toFixed(2)} kW / ${(this.result.targetW / 1000).toFixed(1)} kW`,
      {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '18px',
        color: outputColor,
        fontStyle: 'bold',
      },
    );
    const budgetColor = this.result.passBudget ? HEX.white : HEX.red;
    this.add
      .text(cardX + cardW - 36, statY, 'Spent', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '11px',
        color: '#a1a1a1',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0);
    this.add
      .text(cardX + cardW - 36, statY + 14, `$${this.result.spent} / $${this.result.budget}`, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '18px',
        color: budgetColor,
        fontStyle: 'bold',
      })
      .setOrigin(1, 0);

    // Storage row
    if (typeof this.result.storageKWh === 'number' && typeof this.result.targetKWh === 'number') {
      const storageY = statY + 50;
      const storageColor = this.result.passStorage ? '#57c47a' : HEX.red;
      this.add.text(cardX + 36, storageY, 'Storage', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '11px',
        color: '#a1a1a1',
        fontStyle: 'bold',
      });
      this.add.text(
        cardX + 36,
        storageY + 14,
        `${this.result.storageKWh} kWh / ${this.result.targetKWh} kWh`,
        {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '18px',
          color: storageColor,
          fontStyle: 'bold',
        },
      );
      // Battery sun warning on the right side
      if ((this.result.batsInSun ?? 0) > 0) {
        this.add
          .text(cardX + cardW - 36, storageY, '⚠ Heat penalty', {
            fontFamily: 'Albert Sans, sans-serif',
            fontSize: '11px',
            color: HEX.red,
            fontStyle: 'bold',
          })
          .setOrigin(1, 0);
        this.add
          .text(
            cardX + cardW - 36,
            storageY + 14,
            `${this.result.batsInSun} ${this.result.batsInSun === 1 ? 'battery' : 'batteries'} in sun`,
            {
              fontFamily: 'Albert Sans, sans-serif',
              fontSize: '13px',
              color: HEX.red,
            },
          )
          .setOrigin(1, 0);
      }
    }

    // Time row + best-time persistence
    const timeY = statY + 100;
    if (typeof this.result.elapsedMs === 'number') {
      const ms = this.result.elapsedMs;
      let isNewBest = false;
      const prevBest = getBest(this.result.levelIndex + 1);
      if (this.result.win) isNewBest = recordTime(this.result.levelIndex + 1, ms);
      this.add
        .text(cardX + 36, timeY, 'Time', {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '11px',
          color: '#a1a1a1',
          fontStyle: 'bold',
        });
      this.add.text(cardX + 36, timeY + 14, formatTime(ms), {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '18px',
        color: HEX.white,
        fontStyle: 'bold',
      });
      const bestNow = getBest(this.result.levelIndex + 1);
      const bestStr = bestNow != null ? formatTime(bestNow) : '—';
      this.add
        .text(cardX + cardW - 36, timeY, isNewBest ? 'NEW BEST' : 'Best', {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '11px',
          color: isNewBest ? HEX.amber : '#a1a1a1',
          fontStyle: 'bold',
        })
        .setOrigin(1, 0);
      this.add
        .text(cardX + cardW - 36, timeY + 14, bestStr, {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '18px',
          color: isNewBest ? HEX.amber : HEX.white,
          fontStyle: 'bold',
        })
        .setOrigin(1, 0);
      void prevBest;
    }

    // Buttons
    const btnY = cardY + cardH - 70;
    const btnH = 40;
    const isLast = this.result.levelIndex >= LEVELS.length - 1;
    if (this.result.win) {
      this.makeBtn('RETRY', cardX + 36, btnY, 130, btnH, BRAND.greyDark, () =>
        this.scene.start('LevelScene', { levelIndex: this.result.levelIndex }),
      );
      if (isLast) {
        this.makeBtn(
          'BACK TO MENU',
          cardX + cardW - 36 - 200,
          btnY,
          200,
          btnH,
          BRAND.red,
          () => this.scene.start('MenuScene'),
        );
      } else {
        this.makeBtn(
          'NEXT LEVEL →',
          cardX + cardW - 36 - 180,
          btnY,
          180,
          btnH,
          BRAND.red,
          () => this.scene.start('BriefingScene', { levelIndex: this.result.levelIndex + 1 }),
        );
      }
    } else {
      this.makeBtn('MENU', cardX + 36, btnY, 130, btnH, BRAND.greyDark, () =>
        this.scene.start('MenuScene'),
      );
      this.makeBtn(
        'TRY AGAIN',
        cardX + cardW - 36 - 180,
        btnY,
        180,
        btnH,
        BRAND.red,
        () => this.scene.start('LevelScene', { levelIndex: this.result.levelIndex }),
      );
    }
  }

  private drawStar(cx: number, cy: number, r: number, filled: boolean): Phaser.GameObjects.Graphics {
    const points: number[] = [];
    for (let i = 0; i < 10; i++) {
      const angle = (Math.PI / 5) * i - Math.PI / 2;
      const radius = i % 2 === 0 ? r : r * 0.45;
      points.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
    }
    const g = this.add.graphics();
    g.setPosition(cx, cy);
    if (filled) {
      // Subtle drop-shadow
      g.fillStyle(0x000000, 0.4);
      g.fillPoints(this.toPoints(points, 0, 2), true);
      g.fillStyle(BRAND.amber, 1);
      g.fillPoints(this.toPoints(points), true);
      g.lineStyle(1, 0xffffff, 0.4);
      g.strokePoints(this.toPoints(points), true);
    } else {
      g.lineStyle(2, BRAND.greyDark, 1);
      g.strokePoints(this.toPoints(points), true);
    }
    return g;
  }

  private toPoints(flat: number[], dx = 0, dy = 0): Phaser.Geom.Point[] {
    const pts: Phaser.Geom.Point[] = [];
    for (let i = 0; i < flat.length; i += 2) {
      pts.push(new Phaser.Geom.Point(flat[i] + dx, flat[i + 1] + dy));
    }
    return pts;
  }

  private makeBtn(
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    color: number,
    onClick: () => void,
  ) {
    const c = this.add.container(x, y);
    const bg = this.add.graphics();
    bg.fillStyle(color, 1);
    bg.fillRoundedRect(0, 0, w, h, 6);
    c.add(bg);
    const t = this.add
      .text(w / 2, h / 2, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '14px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    c.add(t);
    bg.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    bg.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(0, 0, w, h, 6);
      bg.lineStyle(2, BRAND.amber, 1);
      bg.strokeRoundedRect(0, 0, w, h, 6);
    });
    bg.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(0, 0, w, h, 6);
    });
    bg.on('pointerdown', onClick);
  }
}
